import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ChevronRight, Building2, Archive, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ConfirmationDialog } from "./ConfirmationDialog";

interface Client {
  id: string;
  name: string;
  created_at: string;
  archived?: boolean;
}

interface AdminClientsTableProps {
  onSelectClient: (client: Client) => void;
  onCreateClick: () => void;
}

const fmtDate = (ts?: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy");
};

export const AdminClientsTable = ({ onSelectClient, onCreateClick }: AdminClientsTableProps) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Client | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('admin_list_clients');
      if (error) throw error;
      const sorted = (data || []).sort((a: Client, b: Client) =>
        a.name.localeCompare(b.name)
      );
      setClients(sorted);
    } catch (error) {
      console.error("Error calling admin_list_clients RPC:", error);
      toast({
        title: "Error",
        description: "Failed to load client organizations via RPC",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (reason: string) => {
    if (!archiveTarget) return;
    setActionLoading(true);
    try {
      const { error } = await (supabase as any).rpc('admin_archive_client', {
        p_client_id: archiveTarget.id,
        p_reason: reason || null,
      });
      if (error) throw error;
      toast({ title: "Client archived", description: `${archiveTarget.name} has been archived.` });
      setArchiveTarget(null);
      await loadClients();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to archive client", variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (reason: string) => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      const { error } = await (supabase as any).rpc('admin_delete_client', {
        p_client_id: deleteTarget.id,
        p_reason: reason || null,
      });
      if (error) throw error;
      toast({ title: "Client deleted", description: `${deleteTarget.name} has been permanently deleted.` });
      setDeleteTarget(null);
      await loadClients();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete client", variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const matchesFilter = !filter || c.name?.toLowerCase().includes(filter.toLowerCase());
    const matchesArchived = showArchived || !c.archived;
    return matchesFilter && matchesArchived;
  });

  if (loading) {
    return <div className="flex justify-center p-8">Loading client organizations...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <Input
          placeholder="Search clients..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex items-center space-x-2">
          <Switch id="show-archived-clients" checked={showArchived} onCheckedChange={setShowArchived} />
          <Label htmlFor="show-archived-clients">Show Archived</Label>
        </div>
        <Button onClick={onCreateClick}>
          Create Organization
        </Button>
      </div>

      {filteredClients.length === 0 ? (
        <div className="py-10 text-sm text-muted-foreground text-center">
          No client organizations found.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client Organization</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelectClient(client)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{client.name}</div>
                        <div className="text-xs text-muted-foreground">{client.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{fmtDate(client.created_at)}</TableCell>
                  <TableCell>
                    {client.archived ? (
                      <Badge variant="secondary">Archived</Badge>
                    ) : (
                      <Badge variant="default">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {!client.archived && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setArchiveTarget(client)}
                          title="Archive client"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteTarget(client)}
                        title="Delete client"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmationDialog
        open={!!archiveTarget}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
        title={`Archive "${archiveTarget?.name}"?`}
        description="This will hide the client from active lists. Sub-organizations, portfolios, and assessments will be preserved. You can restore it later."
        onConfirm={handleArchive}
        confirmText="Archive Client"
        loading={actionLoading}
      />

      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Permanently Delete "${deleteTarget?.name}"?`}
        description="⚠️ This is irreversible. The client will be permanently removed along with its memberships. This will fail if the client has existing sub-organizations or portfolios — archive is recommended instead."
        onConfirm={handleDelete}
        confirmText="Delete Permanently"
        variant="destructive"
        loading={actionLoading}
      />
    </div>
  );
};